import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";

const AgentPointsLogContext = createContext();

export const useAgentPointsLog = () => {
  const context = useContext(AgentPointsLogContext);
  if (!context) {
    throw new Error(
      "useAgentPointsLog must be used within a AgentPointsLogProvider"
    );
  }
  return context;
};

export const AgentPointsLogProvider = ({ children }) => {
  const [pointsLog, setPointsLog] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    currentPage: 1,
    totalPages: 1,
    agentId: null,
  });

  const queryString = buildQueryString({
    page: filterOptions.currentPage,
    include: "agent,season",
    [`filter[agent_id]`]: filterOptions.agentId,
  });

  const fetchPointsLog = useCallback(async () => {
    if (!filterOptions.agentId) {
      setPointsLog({
        data: [],
        pagination: null,
        error: null,
        loading: false,
      });
      return;
    }

    setPointsLog((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/agent-points-log${queryString}`);
      if (response.success) {
        setPointsLog({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination.last_page,
        }));
      }
    } catch (error) {
      setPointsLog({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [filterOptions.agentId, queryString]);

  useEffect(() => {
    fetchPointsLog();
  }, [fetchPointsLog]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const setAgentId = (agentId) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      agentId,
      currentPage: 1, // Reset to first page when changing agent
    }));
  };

  const totalPages = pointsLog.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return (
    <AgentPointsLogContext.Provider
      value={{
        pointsLog,
        refetch: fetchPointsLog,
        fetchPointsLog,
        setAgentId,
        Pagination,
      }}
    >
      {children}
    </AgentPointsLogContext.Provider>
  );
};
